import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  CheckCheck,
  XCircle,
  Archive,
  ShieldAlert,
  Wrench,
  Cpu,
  ExternalLink,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import DriverStatusBadge from './DriverStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:  { label: 'Pending',  cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  archived: { label: 'Archived', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const SAFETY_META = {
  safe_for_mvp:          { label: 'Safe for MVP', cls: 'bg-emerald-50 text-emerald-700' },
  needs_user_consent:    { label: 'Needs user consent', cls: 'bg-indigo-50 text-indigo-700' },
  needs_installer_check: { label: 'Needs installer check', cls: 'bg-amber-50 text-amber-700' },
  read_only:             { label: 'Read-only', cls: 'bg-teal-50 text-teal-700' },
  not_supported_yet:     { label: 'Not supported yet', cls: 'bg-slate-100 text-slate-700' },
  risky_do_not_enable:   { label: 'RISKY — do not enable', cls: 'bg-rose-100 text-rose-800' },
};

const COMPONENT_TYPES = [
  'p1_meter', 'smart_plug', 'solar_panels', 'inverter', 'home_battery',
  'ev_charger', 'heat_pump', 'boiler', 'airco', 'smart_thermostat',
  'fuse_box_3_phase', 'insulation', 'ventilation', 'glass', 'other',
];
const CONTROL_LEVELS = ['none', 'advice_only', 'manual_confirmation', 'limited_control', 'automatic_safe_control', 'installer_locked'];

const CHECKLIST_ITEMS = [
  { key: 'understood', label: 'Driver is technisch begrepen' },
  { key: 'documentation', label: 'Documentatie aanwezig of bekend' },
  { key: 'auto_control_safe', label: 'Automatische sturing is uitgesloten of expliciet veilig' },
  { key: 'consent_clear', label: 'Klanttoestemming is duidelijk indien nodig' },
  { key: 'installer_clear', label: 'Installateurvereiste is duidelijk indien nodig' },
  { key: 'copy_honest', label: 'Klanttekst is eerlijk, geen overpromise' },
];

const WARNINGS = {
  read_only: 'Read-only driver — alleen meten en adviseren, geen sturing.',
  advice_only: 'Advice-only driver — Powersure geeft advies, klant voert zelf uit.',
  installer_locked: 'Installateur vereist — niet zelfstandig activeerbaar door klant.',
  needs_installer_check: 'Installateurcheck vereist vóór gebruik.',
  not_supported: 'Connectie nog niet ondersteund — alleen handmatige route.',
  risky_do_not_enable: 'RISKY — niet voor klantgebruik geschikt in MVP.',
  automatic_safe_control: 'Automatische sturing — extra safety review vereist.',
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${m.cls}`}>{m.label}</span>;
}

function SafetyPill({ status }) {
  const m = SAFETY_META[status] || SAFETY_META.not_supported_yet;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${m.cls}`}>{m.label}</span>;
}

export default function DriversTab({ token }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [filters, setFilters] = useState({
    status: '', component_type: '', control_level: '', safety_status: '',
    approved: '', visible: '',
  });
  const [selected, setSelected] = useState(null);
  const [checklist, setChecklist] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.component_type) params.component_type = filters.component_type;
      const r = await axios.get(`${API}/admin/drivers`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setDrivers(r.data.drivers || []);
    } catch (e) {
      toast.error('Kon drivers niet laden');
    } finally {
      setLoading(false);
    }
  }, [token, filters.status, filters.component_type]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => drivers.filter(d => {
    if (filters.control_level && d.control_level !== filters.control_level) return false;
    if (filters.safety_status && d.safety_status !== filters.safety_status) return false;
    if (filters.approved === 'yes' && !d.approved) return false;
    if (filters.approved === 'no' && d.approved) return false;
    if (filters.visible === 'yes' && !d.visible) return false;
    if (filters.visible === 'no' && d.visible) return false;
    return true;
  }), [drivers, filters]);

  const counts = useMemo(() => ({
    total: drivers.length,
    pending: drivers.filter(d => d.status === 'pending').length,
    approved: drivers.filter(d => d.status === 'approved').length,
    visible: drivers.filter(d => d.visible).length,
    risky: drivers.filter(d => d.safety_status === 'risky_do_not_enable').length,
  }), [drivers]);

  const runSeed = async () => {
    if (!window.confirm('Drivers seeden (idempotent — voegt alleen toe als leeg)?')) return;
    setSeeding(true);
    try {
      const r = await axios.post(`${API}/admin/drivers/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Seed klaar: ${r.data.inserted} toegevoegd`);
      load();
    } catch (e) {
      toast.error('Seed mislukt');
    } finally {
      setSeeding(false);
    }
  };

  const setAction = async (driver, action) => {
    const url = `${API}/admin/drivers/${driver.driver_id}/${action}`;
    try {
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${action} OK`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.validation_errors?.join(', ')
        || e?.response?.data?.detail || `${action} mislukt`;
      toast.error(String(msg));
    }
  };

  const patchDriver = async (driver, patch) => {
    try {
      await axios.patch(`${API}/admin/drivers/${driver.driver_id}`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Driver bijgewerkt');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const errs = detail?.validation_errors;
      toast.error(errs ? errs.join('; ') : (typeof detail === 'string' ? detail : 'Update mislukt'));
    }
  };

  const setVisible = (driver, visible) => {
    if (visible && !driver.approved) {
      toast.error('Kan visible niet aanzetten: driver is nog niet approved.');
      return;
    }
    if (visible && driver.safety_status === 'risky_do_not_enable') {
      toast.error('Risky driver mag nooit visible worden.');
      return;
    }
    if (visible && driver.connection_status === 'not_supported') {
      toast.error('Not-supported driver mag nooit visible worden.');
      return;
    }
    patchDriver(driver, { visible });
  };

  const openDetail = (d) => {
    setSelected(d);
    setChecklist({});
  };

  const allChecked = CHECKLIST_ITEMS.every(item => checklist[item.key]);

  const doApprove = async () => {
    if (!selected || !allChecked) return;
    await setAction(selected, 'approve');
    setSelected(null);
  };

  return (
    <div className="space-y-4" data-testid="drivers-tab">
      {/* Header / counts */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-medium">Drivers / Integraties</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-muted" data-testid="drivers-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          <button onClick={runSeed} disabled={seeding} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-muted" data-testid="drivers-seed-btn">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : '🌱'} Seed
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
        <div className="border rounded p-3" data-testid="drivers-count-total">Totaal<div className="text-2xl font-light">{counts.total}</div></div>
        <div className="border rounded p-3 bg-amber-50">Pending<div className="text-2xl font-light text-amber-800">{counts.pending}</div></div>
        <div className="border rounded p-3 bg-emerald-50">Approved<div className="text-2xl font-light text-emerald-800">{counts.approved}</div></div>
        <div className="border rounded p-3 bg-sky-50">Visible<div className="text-2xl font-light text-sky-800">{counts.visible}</div></div>
        <div className="border rounded p-3 bg-rose-50">Risky<div className="text-2xl font-light text-rose-800">{counts.risky}</div></div>
      </div>

      {/* Filters */}
      <div className="border rounded p-3 grid grid-cols-2 md:grid-cols-6 gap-2 text-sm" data-testid="drivers-filters">
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="border rounded px-2 py-1" data-testid="filter-status">
          <option value="">Alle status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="archived">Archived</option>
        </select>
        <select value={filters.component_type} onChange={e => setFilters(f => ({ ...f, component_type: e.target.value }))} className="border rounded px-2 py-1" data-testid="filter-component-type">
          <option value="">Alle types</option>
          {COMPONENT_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.control_level} onChange={e => setFilters(f => ({ ...f, control_level: e.target.value }))} className="border rounded px-2 py-1" data-testid="filter-control-level">
          <option value="">Alle control levels</option>
          {CONTROL_LEVELS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.safety_status} onChange={e => setFilters(f => ({ ...f, safety_status: e.target.value }))} className="border rounded px-2 py-1" data-testid="filter-safety-status">
          <option value="">Alle safety</option>
          {Object.keys(SAFETY_META).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.approved} onChange={e => setFilters(f => ({ ...f, approved: e.target.value }))} className="border rounded px-2 py-1" data-testid="filter-approved">
          <option value="">Approved?</option>
          <option value="yes">Approved</option>
          <option value="no">Not approved</option>
        </select>
        <select value={filters.visible} onChange={e => setFilters(f => ({ ...f, visible: e.target.value }))} className="border rounded px-2 py-1" data-testid="filter-visible">
          <option value="">Visible?</option>
          <option value="yes">Visible</option>
          <option value="no">Not visible</option>
        </select>
      </div>

      {/* Table */}
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="p-2">Naam</th>
              <th className="p-2">Type</th>
              <th className="p-2">Integratie</th>
              <th className="p-2">Control</th>
              <th className="p-2">Safety</th>
              <th className="p-2">Conn</th>
              <th className="p-2">Status</th>
              <th className="p-2 text-center">Approved</th>
              <th className="p-2 text-center">Visible</th>
              <th className="p-2">Actie</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">Geen drivers gevonden.</td></tr>
            )}
            {filtered.map(d => (
              <tr key={d.driver_id} className="border-t hover:bg-muted/50" data-testid={`driver-row-${d.driver_id}`}>
                <td className="p-2">
                  <button onClick={() => openDetail(d)} className="text-primary hover:underline text-left" data-testid={`driver-name-${d.driver_id}`}>
                    {d.name}
                  </button>
                  <div className="text-xs text-muted-foreground flex gap-1 flex-wrap mt-0.5">
                    {(d.supported_brands || []).slice(0, 3).map(b => <span key={b} className="px-1 bg-muted rounded">{b}</span>)}
                    {d.requires_installer && <span className="inline-flex items-center gap-0.5 px-1 bg-amber-50 text-amber-700 rounded"><Wrench className="w-3 h-3" /> installer</span>}
                    {d.requires_customer_token && <span className="px-1 bg-indigo-50 text-indigo-700 rounded">token</span>}
                    {d.requires_local_network && <span className="px-1 bg-sky-50 text-sky-700 rounded">LAN</span>}
                  </div>
                </td>
                <td className="p-2">{d.component_type}</td>
                <td className="p-2 text-xs">{d.integration_type}</td>
                <td className="p-2 text-xs">{d.control_level}</td>
                <td className="p-2"><SafetyPill status={d.safety_status} /></td>
                <td className="p-2 text-xs">{d.connection_status}</td>
                <td className="p-2"><StatusPill status={d.status} /></td>
                <td className="p-2 text-center">{d.approved ? <CheckCheck className="w-4 h-4 text-emerald-600 inline" /> : <XCircle className="w-4 h-4 text-slate-400 inline" />}</td>
                <td className="p-2 text-center">
                  <Switch
                    checked={!!d.visible}
                    onCheckedChange={(v) => setVisible(d, v)}
                    disabled={!d.approved || d.safety_status === 'risky_do_not_enable' || d.connection_status === 'not_supported'}
                    data-testid={`driver-visible-toggle-${d.driver_id}`}
                  />
                </td>
                <td className="p-2 text-xs whitespace-nowrap">
                  <button onClick={() => openDetail(d)} className="text-primary hover:underline" data-testid={`driver-review-btn-${d.driver_id}`}>Review</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="driver-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.name}
                  <StatusPill status={selected.status} />
                </DialogTitle>
                <DialogDescription>{selected.component_type} · {selected.integration_type}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                {/* Customer label preview */}
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">Klant ziet</div>
                  <DriverStatusBadge driver={selected} size="lg" />
                </div>

                {/* Warnings */}
                <div className="space-y-1">
                  {WARNINGS[selected.safety_status] && (
                    <div className="flex items-start gap-2 p-2 rounded border bg-amber-50 text-amber-900" data-testid="warning-safety">
                      <ShieldAlert className="w-4 h-4 mt-0.5" /> {WARNINGS[selected.safety_status]}
                    </div>
                  )}
                  {WARNINGS[selected.control_level] && (
                    <div className="flex items-start gap-2 p-2 rounded border bg-sky-50 text-sky-900" data-testid="warning-control">
                      <ShieldAlert className="w-4 h-4 mt-0.5" /> {WARNINGS[selected.control_level]}
                    </div>
                  )}
                  {selected.connection_status === 'not_supported' && (
                    <div className="flex items-start gap-2 p-2 rounded border bg-slate-50 text-slate-900" data-testid="warning-conn">
                      <ShieldAlert className="w-4 h-4 mt-0.5" /> {WARNINGS.not_supported}
                    </div>
                  )}
                </div>

                {/* Sections */}
                <div className="grid grid-cols-2 gap-3">
                  <Section title="Basis">
                    <Row k="Segment" v={selected.segment} />
                    <Row k="Type" v={selected.component_type} />
                    <Row k="Integratie" v={selected.integration_type} />
                    <Row k="Control level" v={selected.control_level} />
                    <Row k="Safety status" v={selected.safety_status} />
                    <Row k="Connection" v={selected.connection_status} />
                  </Section>
                  <Section title="Data capabilities">
                    <ListPills items={selected.data_capabilities || []} empty="Geen" />
                  </Section>
                  <Section title="Control capabilities">
                    <ListPills items={selected.control_capabilities || []} empty="Geen" />
                  </Section>
                  <Section title="Vereisten">
                    <Row k="Installateur" v={selected.requires_installer ? 'ja' : 'nee'} />
                    <Row k="Customer token" v={selected.requires_customer_token ? 'ja' : 'nee'} />
                    <Row k="LAN nodig" v={selected.requires_local_network ? 'ja' : 'nee'} />
                  </Section>
                </div>

                {selected.limitations && (
                  <Section title="Beperkingen">
                    <div className="text-sm text-foreground/80">{selected.limitations}</div>
                  </Section>
                )}
                {selected.consent_text && (
                  <Section title="Klanttekst / consent">
                    <div className="text-sm text-foreground/80 italic">"{selected.consent_text}"</div>
                  </Section>
                )}
                {selected.documentation_url && (
                  <a href={selected.documentation_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary text-sm">
                    <ExternalLink className="w-3.5 h-3.5" /> Documentatie
                  </a>
                )}

                {/* Approval checklist */}
                {selected.status !== 'approved' && (
                  <div className="border-t pt-3">
                    <div className="text-xs uppercase text-muted-foreground mb-2">Approval checklist</div>
                    <div className="space-y-1">
                      {CHECKLIST_ITEMS.map(item => (
                        <label key={item.key} className="flex items-start gap-2 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={!!checklist[item.key]}
                            onChange={(e) => setChecklist(c => ({ ...c, [item.key]: e.target.checked }))}
                            data-testid={`checklist-${item.key}`}
                          />
                          {item.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex justify-between flex-wrap gap-2">
                <div className="flex gap-2">
                  <button onClick={() => setAction(selected, 'reject')} className="px-3 py-1.5 text-sm border border-rose-300 text-rose-700 rounded hover:bg-rose-50" data-testid="driver-reject-btn">
                    <XCircle className="w-4 h-4 inline mr-1" /> Reject
                  </button>
                  <button onClick={() => patchDriver(selected, { status: 'archived', visible: false, approved: false })} className="px-3 py-1.5 text-sm border rounded hover:bg-muted" data-testid="driver-archive-btn">
                    <Archive className="w-4 h-4 inline mr-1" /> Archive
                  </button>
                </div>
                <div className="flex gap-2">
                  {selected.visible
                    ? <button onClick={() => setVisible(selected, false)} className="px-3 py-1.5 text-sm border rounded" data-testid="driver-hide-btn"><EyeOff className="w-4 h-4 inline mr-1" /> Verbergen</button>
                    : <button onClick={() => setVisible(selected, true)} className="px-3 py-1.5 text-sm border rounded disabled:opacity-40" disabled={!selected.approved || selected.safety_status === 'risky_do_not_enable' || selected.connection_status === 'not_supported'} data-testid="driver-show-btn"><Eye className="w-4 h-4 inline mr-1" /> Maak zichtbaar</button>
                  }
                  {selected.status !== 'approved' && (
                    <button
                      onClick={doApprove}
                      disabled={!allChecked}
                      className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="driver-approve-btn"
                    >
                      <CheckCheck className="w-4 h-4 inline mr-1" /> Approve {allChecked ? '' : `(${CHECKLIST_ITEMS.filter(i => !checklist[i.key]).length} open)`}
                    </button>
                  )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border rounded p-3">
      <div className="text-xs uppercase text-muted-foreground mb-1">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between gap-2 text-xs"><span className="text-muted-foreground">{k}</span><span className="font-mono">{String(v ?? '—')}</span></div>;
}
function ListPills({ items, empty }) {
  if (!items?.length) return <div className="text-xs text-muted-foreground">{empty}</div>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(i => <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-muted">{i}</span>)}
    </div>
  );
}
